import { useEffect, useState } from "react";
import { format } from "date-fns";
import {
  Download,
  Loader2,
  Plus,
  Receipt,
  RefreshCw,
  Wallet,
} from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { usesWalletBillingForMerchant } from "@/lib/billing";
import { formatCurrency } from "@/lib/currency";
import { fetchSupabaseFunction } from "@/lib/shopifySessionToken";
import { GST_MODULE_ENABLED } from "@/lib/featureFlags";

interface Props {
  billingProvider?: string | null;
  merchantId: string;
  platformType?: string | null;
  signupSource?: string | null;
}

interface WalletLedgerEntry {
  amount: number;
  balance_after: number;
  created_at: string;
  currency: string;
  description: string;
  entry_kind: string;
  held_balance_after: number;
  id: string;
}

interface CommissionInvoice {
  currency: string;
  generated_at: string;
  gst_amount: number;
  gst_rate: number;
  id: string;
  invoice_number: string;
  paid_at: string | null;
  product_title: string;
  status: string;
  subtotal: number;
  total_amount: number;
}

interface WalletDashboardResponse {
  commission: {
    freeSplitsRemaining: number;
    includedFreeSplits: number;
    overageFeePercent: number;
    usesWallet: boolean;
  };
  commissionInvoices: CommissionInvoice[];
  ledgerEntries: WalletLedgerEntry[];
  merchant: {
    currency: string;
    plan: string;
    splitsThisMonth: number;
    status: string;
  };
  success: boolean;
  wallet: {
    availableBalance: number;
    currency: string;
    heldBalance: number;
    totalBalance: number;
  };
}

function getEntryLabel(entryKind: string): string {
  switch (entryKind) {
    case "topup":
      return "Wallet top-up";
    case "hold":
      return "Commission reserved";
    case "release":
      return "Reservation released";
    case "commission_debit":
      return "Commission charged";
    default:
      return entryKind.replace(/_/g, " ");
  }
}

function getEntryBadge(entryKind: string) {
  if (entryKind === "topup" || entryKind === "release") {
    return <Badge className="bg-green-500/10 text-green-700 border-green-500/20">Credit</Badge>;
  }

  if (entryKind === "hold") {
    return <Badge className="bg-amber-500/10 text-amber-700 border-amber-500/20">Held</Badge>;
  }

  return <Badge className="bg-blue-500/10 text-blue-700 border-blue-500/20">Debit</Badge>;
}

function getCurrencyLabel(currency: string): string {
  switch (currency) {
    case "USD":
      return "USD 100";
    case "EUR":
      return "EUR 100";
    case "GBP":
      return "GBP 100";
    case "AUD":
      return "AUD 100";
    case "CAD":
      return "CAD 100";
    case "INR":
    default:
      return "INR 100";
  }
}

export function MerchantWalletPanel({ billingProvider, merchantId, platformType, signupSource }: Props) {
  const [dashboard, setDashboard] = useState<WalletDashboardResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [downloadingInvoiceId, setDownloadingInvoiceId] = useState<string | null>(null);
  const [processingTopup, setProcessingTopup] = useState(false);
  const [topupAmount, setTopupAmount] = useState("2500");

  const supportsWallet = usesWalletBillingForMerchant({
    billing_provider: billingProvider,
    platform_type: platformType,
    signup_source: signupSource,
  });

  const loadDashboard = async (silent = false) => {
    if (silent) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    try {
      const response = await fetchSupabaseFunction("get-wallet-dashboard", {
        body: { merchantId },
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || "Failed to load wallet details");
      }

      const data = await response.json() as WalletDashboardResponse;
      setDashboard(data);
    } catch (error: any) {
      console.error("Error loading wallet dashboard:", error);
      toast.error(error.message || "Failed to load wallet details");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (!supportsWallet) {
      setLoading(false);
      return;
    }

    loadDashboard();
  }, [merchantId, supportsWallet]);

  const loadRazorpayScript = (): Promise<boolean> => {
    return new Promise((resolve) => {
      if ((window as any).Razorpay) {
        resolve(true);
        return;
      }

      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handleTopup = async () => {
    const amount = Number(topupAmount || 0);
    const topupCurrency = dashboard?.wallet.currency || dashboard?.merchant.currency || "INR";
    if (!Number.isFinite(amount) || amount < 100) {
      toast.error(`Enter a wallet top-up amount of at least ${getCurrencyLabel(topupCurrency)}`);
      return;
    }

    setProcessingTopup(true);
    try {
      const orderResponse = await fetchSupabaseFunction("create-wallet-topup", {
        body: { amount, merchantId },
      });

      const orderPayload = await orderResponse.json().catch(() => ({}));
      if (!orderResponse.ok) {
        throw new Error(orderPayload.error || "Failed to create wallet top-up");
      }

      const loaded = await loadRazorpayScript();
      if (!loaded) {
        throw new Error("Unable to load Razorpay checkout");
      }

      const razorpay = new (window as any).Razorpay({
        amount: Math.round(Number(orderPayload.amount || amount) * 100),
        currency: orderPayload.currency || "INR",
        description: "Splitkart wallet top-up",
        handler: async (paymentResponse: any) => {
          try {
            const verifyResponse = await fetchSupabaseFunction("verify-wallet-topup", {
              body: {
                merchantId,
                razorpay_order_id: paymentResponse.razorpay_order_id,
                razorpay_payment_id: paymentResponse.razorpay_payment_id,
                razorpay_signature: paymentResponse.razorpay_signature,
              },
            });

            const verifyPayload = await verifyResponse.json().catch(() => ({}));
            if (!verifyResponse.ok) {
              throw new Error(verifyPayload.error || "Wallet top-up verification failed");
            }

            toast.success("Wallet topped up successfully");
            await loadDashboard(true);
          } catch (error: any) {
            console.error("Wallet top-up verification failed:", error);
            toast.error(error.message || "Payment received but wallet top-up verification failed");
          } finally {
            setProcessingTopup(false);
          }
        },
        key: orderPayload.keyId,
        name: "Splitkart",
        notes: {
          invoice_number: orderPayload.invoiceNumber || "",
        },
        order_id: orderPayload.razorpayOrderId,
        prefill: {},
        theme: {
          color: "#3F51B5",
        },
      });

      razorpay.on("payment.failed", (event: any) => {
        setProcessingTopup(false);
        toast.error(event?.error?.description || "Wallet top-up payment failed");
      });

      razorpay.open();
    } catch (error: any) {
      console.error("Wallet top-up failed:", error);
      toast.error(error.message || "Failed to top up wallet");
      setProcessingTopup(false);
    }
  };

  const downloadCommissionInvoice = async (invoice: CommissionInvoice) => {
    setDownloadingInvoiceId(invoice.id);
    try {
      const response = await fetchSupabaseFunction("generate-commission-invoice", {
        body: { invoiceId: invoice.id },
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || "Failed to generate commission invoice");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `${invoice.invoice_number}.pdf`;
      anchor.click();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error("Error downloading commission invoice:", error);
      toast.error(error.message || "Failed to download commission invoice");
    } finally {
      setDownloadingInvoiceId(null);
    }
  };

  if (!supportsWallet) {
    return null;
  }

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Wallet className="h-5 w-5" />
            Splitkart Wallet
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-40 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (!dashboard) {
    return null;
  }

  const currency = dashboard.wallet.currency || dashboard.merchant.currency || "INR";

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Wallet className="h-5 w-5" />
              Splitkart Wallet
            </CardTitle>
            <CardDescription>
              Splitkart commissions are reserved when a split is created and debited from this wallet after successful completion.
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={() => loadDashboard(true)} disabled={refreshing}>
            {refreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          </Button>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid gap-4 md:grid-cols-3">
            <div className="rounded-lg border p-4">
              <p className="text-sm text-muted-foreground">Available balance</p>
              <p className="mt-2 text-2xl font-semibold">{formatCurrency(dashboard.wallet.availableBalance, currency as any)}</p>
            </div>
            <div className="rounded-lg border p-4">
              <p className="text-sm text-muted-foreground">Held for active splits</p>
              <p className="mt-2 text-2xl font-semibold">{formatCurrency(dashboard.wallet.heldBalance, currency as any)}</p>
            </div>
            <div className="rounded-lg border p-4">
              <p className="text-sm text-muted-foreground">Free splits remaining</p>
              <p className="mt-2 text-2xl font-semibold">{dashboard.commission.freeSplitsRemaining}</p>
              <p className="mt-1 text-xs text-muted-foreground">
                {dashboard.merchant.status === "trialing"
                  ? `Intro trial: 0% commission for your first ${dashboard.commission.includedFreeSplits} splits in 30 days`
                  : `${dashboard.commission.overageFeePercent}% commission after ${dashboard.commission.includedFreeSplits} free splits`}
              </p>
            </div>
          </div>

          <div className="rounded-lg border p-4">
            <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
              <div className="space-y-2">
                <p className="text-sm font-medium">Top up wallet</p>
                <p className="text-sm text-muted-foreground">
                  Keep enough balance to avoid split creation failures once your free split allowance is exhausted.
                </p>
              </div>
              <div className="flex w-full flex-col gap-2 sm:flex-row md:w-auto">
                <Input
                  type="number"
                  min="100"
                  step="100"
                  value={topupAmount}
                  onChange={(event) => setTopupAmount(event.target.value)}
                  className="sm:w-40"
                />
                <Button onClick={handleTopup} disabled={processingTopup}>
                  {processingTopup ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
                  Add Funds
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Receipt className="h-5 w-5" />
            Wallet Ledger
          </CardTitle>
          <CardDescription>Every wallet top-up, hold, release, and commission debit is recorded here.</CardDescription>
        </CardHeader>
        <CardContent>
          {dashboard.ledgerEntries.length === 0 ? (
            <p className="py-6 text-sm text-muted-foreground">No wallet activity yet.</p>
          ) : (
            <div className="space-y-3">
              {dashboard.ledgerEntries.map((entry) => (
                <div key={entry.id} className="flex items-center justify-between rounded-lg border p-4">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <p className="font-medium">{getEntryLabel(entry.entry_kind)}</p>
                      {getEntryBadge(entry.entry_kind)}
                    </div>
                    <p className="text-sm text-muted-foreground">{entry.description}</p>
                    <p className="text-xs text-muted-foreground">
                      {format(new Date(entry.created_at), "MMM d, yyyy • h:mm a")}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold">{formatCurrency(entry.amount, entry.currency as any)}</p>
                    <p className="text-xs text-muted-foreground">
                      Available: {formatCurrency(entry.balance_after, entry.currency as any)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Held: {formatCurrency(entry.held_balance_after, entry.currency as any)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {GST_MODULE_ENABLED ? (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5" />
            Commission GST Invoices
          </CardTitle>
          <CardDescription>Download tax invoices for completed split commissions deducted from your wallet.</CardDescription>
        </CardHeader>
        <CardContent>
          {dashboard.commissionInvoices.length === 0 ? (
            <p className="py-6 text-sm text-muted-foreground">No commission invoices yet.</p>
          ) : (
            <div className="space-y-3">
              {dashboard.commissionInvoices.map((invoice) => (
                <div key={invoice.id} className="flex items-center justify-between rounded-lg border p-4">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <p className="font-mono text-sm font-medium">{invoice.invoice_number}</p>
                      <Badge className="bg-green-500/10 text-green-700 border-green-500/20">
                        {invoice.status}
                      </Badge>
                    </div>
                    <p className="text-sm">{invoice.product_title}</p>
                    <p className="text-xs text-muted-foreground">
                      GST {invoice.gst_rate}% • {format(new Date(invoice.generated_at), "MMM d, yyyy")}
                    </p>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="font-semibold">{formatCurrency(invoice.total_amount, invoice.currency as any)}</p>
                      <p className="text-xs text-muted-foreground">
                        GST: {formatCurrency(invoice.gst_amount, invoice.currency as any)}
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => downloadCommissionInvoice(invoice)}
                      disabled={downloadingInvoiceId === invoice.id}
                    >
                      {downloadingInvoiceId === invoice.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <Download className="mr-1 h-4 w-4" />
                          PDF
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
          </CardContent>
        </Card>
      ) : null}
      </div>
    );
  }
